import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { AcceptResult, SignatureEvent, SignContextResponse } from '@rheora/enrollment-contract';

import { funnelQueryKeys } from './funnel.queries';
import { getSignContext, postSignAccept } from './sign.fetchers';

/**
 * TanStack hooks for the E-SF06 sign step. Pages call THESE, never the fetchers.
 * The context is a query (a read, session-scoped by `candidateId`); the accept is
 * a mutation (a POST with side effects + an idempotency key).
 */

/**
 * ── THE SIGN CONTEXT IS AN INVALIDATION TARGET, NOT JUST A KEY ─────────────
 *
 * `GET /bff/sign/context` carries the SERVER-AUTHORITATIVE `depositGate`, and the
 * sign step's primary action is gated on it. Any mutation that moves that gate must
 * invalidate THIS key, not merely the funnel snapshot.
 *
 * ⚠️ THE DEFECT THIS EXISTS TO STOP (measured 2026-08-26). The four deposit
 * mutations invalidated `funnelQueryKeys.snapshot()` only. A customer elected a
 * §25.478 waiver, the server moved to `DEPOSIT_WAIVED / satisfied: true` — and the
 * page went on rendering "A deposit is required before you can sign" with the
 * primary action disabled and the sentence "You can sign once your deposit is
 * settled above" beneath it, until the customer manually reloaded. The gate the
 * page reads and the gate the server holds were two different facts.
 *
 * The snapshot invalidation was not merely insufficient, it could not have worked:
 * `FunnelProvider` seeds from the snapshot ONCE and never re-reads it, so nothing
 * on screen was watching it. The sign context is the query the surface actually
 * renders from, which is why it is the one that has to be invalidated. An inline
 * key literal could not be targeted from `deposit.queries` without being written
 * out twice — hence this export.
 */
export const signQueryKeys = {
  /** Everything sign-scoped — the prefix a deposit write invalidates. */
  all: () => ['shop', 'sign'] as const,
  /** The sign context for one resolved candidate. */
  context: (candidateId: string | undefined) => ['shop', 'sign', 'context', candidateId] as const,
} as const;

/** The sign context. Disabled until a resolved `candidateId` is in hand (E-SF03). */
export function useSignContextQuery(
  candidateId: string | undefined,
): UseQueryResult<SignContextResponse> {
  return useQuery({
    queryKey: signQueryKeys.context(candidateId),
    queryFn: () => getSignContext(candidateId ?? ''),
    enabled: candidateId !== undefined && candidateId !== '',
  });
}

/**
 * What one accept SUBMIT carries: the signature event, plus the key of the LOGICAL
 * OPERATION it belongs to.
 *
 * ⚠️ THE KEY IS A MUTATION VARIABLE, NOT SOMETHING THIS HOOK MINTS. The hook cannot
 * know whether it is being called for a new acceptance or for the second click of
 * one already in flight — only the surface that owns the form does. Minting here
 * would reproduce exactly the per-click key QA recorded (QA-08-05-05). The owner is
 * `useSignOperation` / `sign-operation.service.ts`.
 */
export interface SignAcceptSubmission {
  readonly event: SignatureEvent;
  readonly idempotencyKey: string;
}

/**
 * The at-accept saga. On success the funnel is SIGNED, which is the largest state
 * change in the whole flow — so the cached `GET /bff/funnel` snapshot is invalidated
 * and a returning tab reads `step: 'signed'` (and, since 2026-08-26, the
 * `statusToken`) rather than being invited back into a funnel it has already
 * completed.
 *
 * ── ⚠️ `onSuccess` DELIBERATELY RETURNS `void`, NOT THE INVALIDATION PROMISE ─
 * TanStack awaits whatever `onSuccess` returns BEFORE it dispatches the success
 * state to observers. Returning the `invalidateQueries` promise therefore parks the
 * customer on "Signing…" for the duration of the follow-up refetch, on top of an
 * accept measured at 13.9 s and 25.4 s in two real browser walks. Nothing on the
 * confirmation needs the refetch to have landed — the accept's own result carries
 * the status token, the start date and the rescission deadline — so the refresh is
 * fired and the UI is released. The `void` operator is the explicit "not awaited,
 * on purpose" marker; do not "fix" it back into a return.
 */
export function useSignAcceptMutation(): UseMutationResult<
  AcceptResult,
  Error,
  SignAcceptSubmission
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ event, idempotencyKey }: SignAcceptSubmission) =>
      postSignAccept(event, idempotencyKey),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() });
      // The sign context's deposit gate and start-date window describe an enrollment
      // that no longer exists to be signed. Dropping it stops a stale surface being
      // rendered behind the confirmation.
      void queryClient.invalidateQueries({ queryKey: signQueryKeys.all() });
    },
  });
}
